import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import { useState } from "react";
import { getCharges, selectState, setResourceDischargePopover } from "../charges.slice";
import { ICreateDischargeRequestParams } from "Dashboard/scenes/Resources/domain/ICreateDischargeRequestParams";
import { Controller, useForm } from "react-hook-form";
import { DiffDays, FormatDateIso } from "utils/dateUtils";
import { resourcesServices } from "Dashboard/scenes/Resources/services/resource.service";
import { toast } from "react-toastify";
import { selectResourceDetails, updateLastKnownUsageAmount, updateResourceDischarged } from "../../Details/details.slice";
import { consumptionLabel, haveAverageConsumption } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import popoverClasses from "../../../../../../components/Popover/popover.module.scss";
import classes from "../../../styles/ResourceDischargePopover.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumber } from "utils/numberUtils";
import DatePicker from "react-datepicker";

const ResourceDischargePopover = () => {
  const dispatch = useAppDispatch();
  const { dateFrom, dateTo, discharging } = useSelector(selectState);
  const resourceDetails = useSelector(selectResourceDetails);
  const [incorrectUsage, setIncorrectUsage] = useState<boolean>(false);
  const [currentUsage, setCurrentUsage] = useState<number>(0);
  const [incorrectAmountToDischarge, setIncorrectAmountToDischarge] = useState<boolean>(false);
  const [currentAmountToDischarge, setCurrentAmountToDischarge] = useState<number>(0);
  const [incorrectDate, setIncorrectDate] = useState(false);
  const todayAsString = new Date().toISOString().split("T")[0];
  const defaultDischargeDate = discharging !== undefined && discharging.dateCharge > todayAsString ? discharging.dateCharge : todayAsString;

  const close = () => {
    dispatch(setResourceDischargePopover({ isOpen: false }));
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ICreateDischargeRequestParams>();

  const onChangeUsage = (newUsageAmount: number) => {
    setCurrentUsage(newUsageAmount);
    if (resourceDetails && discharging) {
      setIncorrectUsage(newUsageAmount < discharging.usageAmountAtStart);
    }
  }

  const isAmountToDischargeAcceptable = (amountToDischarge: number): boolean => {
    if (resourceDetails === undefined || discharging === undefined) {
      return false;
    }
    return amountToDischarge > 0 && amountToDischarge <= discharging.quantityAmount;
  }

  const onChangeAmountToDischarge = (newAmountToDischarge: number) => {
    setCurrentAmountToDischarge(newAmountToDischarge)
    if (resourceDetails && discharging) {
      setIncorrectAmountToDischarge(!isAmountToDischargeAcceptable(newAmountToDischarge));
    }
  }

  const onChangeDate = (newDate: string | null) => {
    setIncorrectDate(!isDateValid(newDate));
  }

  const isDateValid = (date: string | null) => {
    return discharging && date !== null && DiffDays(date) <= 1 && DiffDays(date, discharging.dateCharge) >= 0;
  }

  const onDischarge = () => {
    if (dateFrom != null && dateTo != null && resourceDetails?.resource.resourceId) {
      dispatch(
        getCharges({
          resourceId: resourceDetails.resource.resourceId,
          dateFrom: FormatDateIso(dateFrom),
          dateTo: FormatDateIso(dateTo),
        })
      );
    }

  }

  const onSubmit = async (data: ICreateDischargeRequestParams) => {
    if (resourceDetails && discharging) {
      if (haveAverageConsumption(resourceDetails.resource.category)
        && discharging.usageAmountAtStart > currentUsage) {
        setIncorrectUsage(true);
        toast.error(`Unesite ${resourceDetails?.resource.usageUnit.name === "km" ? "veću kilometražu ili jednaku onoj sa početka zaduženja" : "veće radne sate ili jednake onim sa početka zaduženja"} (${FormatNumber(discharging.usageAmountAtStart)} ${resourceDetails?.resource.usageUnit.name})!`);
        return;
      }
      if (!haveAverageConsumption(resourceDetails.resource.category)
        && !isAmountToDischargeAcceptable(currentAmountToDischarge)) {
        setIncorrectAmountToDischarge(true);
        toast.error(`Unesite za količinu resursa koju razdužujete broj između 1 i ${discharging.quantityAmount}!`);
        return;
      }
      if (discharging === undefined) {
        toast.error("Neočekivana greška");
        return
      }
      if (!isDateValid(data.date)) {
        setIncorrectDate(true);
        toast.error("Datum ne sme biti pre datuma zaduženja kao ni posle sutrašnjeg dana");
        return;
      }
      try {
        const dischargeResponse = await resourcesServices.createDischarge(discharging.chargeId, data);
        data.usageAmount
          && dispatch(updateLastKnownUsageAmount(data.usageAmount));
        dispatch(updateResourceDischarged(dischargeResponse));
        toast.success("Resurs je uspešno razdužen.");
        close();
        onDischarge();
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`${popoverClasses.basicPopoverView} ${popoverClasses.gPopoverInputs}`}
    >

      <div >
        <div className={`${classes.disclaimer} ${popoverClasses.gPopoverDisclaimer}`}>
          Ovde anulirate zaduženje resursa.<br /> Sledeći korak ne možete vratiti nazad.
        </div>
      </div>
      {resourceDetails && haveAverageConsumption(resourceDetails.resource.category) && (
        <div
          className={`${errors.usageAmount || incorrectUsage === true ? popoverClasses.inputError : ""}`}
        >
          <div>
            <label>
              {" "}
              {resourceDetails.resource &&
                consumptionLabel(resourceDetails.resource.usageUnit)}
            </label>
            <Controller
              name="usageAmount"
              control={control}
              defaultValue={undefined}
              rules={{
                required: true,
              }}
              render={({ field: { onChange } }) => (
                <NumberInput
                  formatAs="quantity"
                  onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                    onChange({ target: { value: parsedValue } });
                    onChangeUsage(parsedValue);
                  }}
                  placeholder={resourceDetails.resource?.lastKnownUsageAmount !== undefined
                    ? FormatNumber(resourceDetails.resource?.lastKnownUsageAmount)
                    : undefined}
                  className={classes.inputField}
                />
              )}
            />
          </div>
        </div>
      )}
      {resourceDetails && !haveAverageConsumption(resourceDetails.resource.category) && (
        <div className={`${errors.amountToDischarge || incorrectAmountToDischarge === true ? popoverClasses.inputError : ""}`}>
          <div>
            <label>količina</label>
            <Controller
              name="amountToDischarge"
              control={control}
              defaultValue={undefined}
              rules={{
                required: true,
              }}
              render={({ field: { onChange } }) => (
                <NumberInput
                  formatAs="quantity"
                  onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                    onChange({ target: { value: parsedValue } });
                    onChangeAmountToDischarge(parsedValue);
                  }}
                  placeholder={discharging?.quantityAmount !== undefined && discharging?.quantityAmount !== null
                    ? FormatNumber(discharging.quantityAmount)
                    : undefined}
                  className={classes.inputField}
                />
              )}
            />
          </div>
        </div>
      )}

      <div className={`${errors.date || incorrectDate ? popoverClasses.inputError : ""}`}>
        <div>
          <label className={classes.aboveLabel}>datum</label>
          <Controller
            name="date"
            control={control}
            defaultValue={defaultDischargeDate}
            render={({ field: { onChange, value } }) => (
              <div className={classes.dateWrapper}>
                <DatePicker
                  onChange={(date) => {
                    const dateAsString = date ? date.toISOString().split("T")[0] : null;
                    onChange(dateAsString);
                    onChangeDate(dateAsString);
                  }}
                  showMonthDropdown
                  showYearDropdown
                  dateFormat="dd.MM.yyyy"
                  selected={value ? new Date(value) : new Date()}
                  className={`${classes.inputField} ${errors.date || incorrectDate ? popoverClasses.inputError : ""}`}
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className={popoverClasses.buttons}>
        <button type="submit" className={popoverClasses.iconButton}>
          <FontAwesomeIcon
            icon={faLongArrowAltDown}
            className={popoverClasses.icon} />
        </button>
      </div>
    </form>
  );
};

export default ResourceDischargePopover;
